import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

/**
 * Hook for managing Academic Students Search
 *
 * Provides functions to:
 * - Fetch students with comprehensive details
 * - Search and filter students
 * - Get detailed student information
 */
export const useAcademicStudents = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [summary, setSummary] = useState({
    total: 0,
    day: 0,
    boarding: 0,
    withTransport: 0,
    clearedTuition: 0,
  });

  /**
   * Fetch students with optional filters and pagination
   */
  const fetchStudents = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const page = params.page || pagination.current;
        const pageSize = params.pageSize || pagination.pageSize;

        const queryObj = {
          page,
          page_size: pageSize,
        };

        // Add filters
        if (params.filters) {
          Object.entries(params.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
              queryObj[key] = value;
            }
          });
        }

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.academics.studentSearch}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          const data = response.data || response;
          setStudents(data.results || []);
          setPagination({
            current: page,
            pageSize: pageSize,
            total: data.count || 0,
          });
          if (data.summary) {
            setSummary(data.summary);
          }
          return data;
        } else {
          Utilities.showError(response, "Failed to fetch students");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while fetching students");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, pagination.current, pagination.pageSize],
  );

  /**
   * Get detailed information for a single student
   * @param {string} identifier - Student ID or SPC
   */
  const fetchStudentDetail = useCallback(
    async (identifier) => {
      setDetailLoading(true);
      try {
        const url = domainSettings.urls.academics.studentSearchDetail.replace(
          ":identifier",
          identifier,
        );
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          const data = response.data || response;
          setSelectedStudent(data);
          return data;
        } else {
          Utilities.showError(response, "Failed to fetch student details");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while fetching student details");
        console.error(error);
        return null;
      } finally {
        setDetailLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Clear selected student
   */
  const clearSelectedStudent = useCallback(() => {
    setSelectedStudent(null);
  }, []);

  /**
   * Handle pagination change
   */
  const handlePaginationChange = useCallback((page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
  }, []);

  return {
    students,
    selectedStudent,
    loading,
    detailLoading,
    pagination,
    summary,
    fetchStudents,
    fetchStudentDetail,
    clearSelectedStudent,
    handlePaginationChange,
    setPagination,
  };
};

export default useAcademicStudents;
